import * as React from 'react'

import { ChevronLeft, MoreVertical } from 'lucide-react'
import { reaction } from 'mobx'
import { observer } from 'mobx-react'

import Avatar from '~/client/src/shared/components/Avatar/Avatar'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { Loader } from '~/client/src/shared/components/Loader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Empty,
  EmptyAction,
  EmptyContent,
  EmptyHeader,
  EmptyTitle,
} from '~/client/src/shared/components/shadcn-ui/empty'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { useFilePreview } from '~/client/src/shared/contexts/FilePreviewContext'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { canArchive, canLeave } from '../core/chatPermissions'
import { ImageUploader } from '../core/ports'
import {
  Attachment,
  Chat,
  ChatMessage,
  Contact,
  messageKey,
} from '../core/protocol/types'
import { isErr, isOk } from '../core/result'
import { AttachmentTrayStore } from '../core/stores/AttachmentTray.store'
import { ChatSession } from '../core/stores/Session'
import { toImagePreviewFiles } from '../web/attachmentPreview'
import { WebContactDirectory } from '../web/directory'
import ComposerBar from './ComposerBar'
import { buildRows, LogRow } from './logRows'
import MessageBubble from './MessageBubble'
import WorkflowCardDataStore from './workflow/WorkflowCardData.store'
import WorkflowLinkCards from './workflow/WorkflowLinkCards'
import { extractWorkflowLinks } from './workflow/workflowLinks'

const LOAD_OLDER_THRESHOLD_PX = 300
// Within this distance of the bottom the reader counts as caught up, and the
// list follows new messages; further away it holds still.
const PIN_THRESHOLD_PX = 40
// A short message makes a narrow bubble column; cards need real width to be
// legible, so they get a fixed envelope like the photo collage does.
const WORKFLOW_CARD_WIDTH = 260

interface IProps {
  session: ChatSession
  directory: WebContactDirectory
  chat: Chat
  projectDateStore: ProjectDateStore
  uploadImage: ImageUploader
  createWorkflowCardStore: () => WorkflowCardDataStore
  shouldSendOnEnter?: boolean
  onMentionPress: (mentionedUserId: string) => void
  onOpenWorkflow: (workflowId: string) => void
  onOpenGroupDetails: (chat: Chat) => void
  onBack: () => void
}

type PendingAction = 'archive' | 'leave'

function ChatLogScreen({
  session,
  directory,
  chat,
  projectDateStore,
  uploadImage,
  createWorkflowCardStore,
  shouldSendOnEnter,
  onMentionPress,
  onOpenWorkflow,
  onOpenGroupDetails,
  onBack,
}: IProps): React.ReactElement {
  const [log] = React.useState(() => session.openChatLog(chat))
  const [tray] = React.useState(
    () => new AttachmentTrayStore(uploadImage, session.config.newId),
  )
  const [workflowCardStore] = React.useState(createWorkflowCardStore)
  const { contacts } = session
  const listRef = React.useRef<HTMLDivElement>(null)
  const isPinnedRef = React.useRef(true)
  const lastScrollHeightRef = React.useRef(0)
  const oldestKeyRef = React.useRef<string>(null)
  const [pendingAction, setPendingAction] = React.useState<PendingAction>(null)
  const [actionError, setActionError] = React.useState<string>(null)
  const { showPreview } = useFilePreview()

  React.useEffect(() => {
    return () => session.closeChatLog(chat.id)
  }, [session, chat.id])

  React.useEffect(() => {
    const disposer = reaction(
      () =>
        log.sortedMessages.flatMap(message =>
          message.text
            ? extractWorkflowLinks(message.text, window.location.host)
            : [],
        ),
      links => workflowCardStore.loadCards(links),
      { fireImmediately: true },
    )
    return () => {
      disposer()
      workflowCardStore.dispose()
    }
  }, [log, workflowCardStore])

  React.useEffect(() => {
    if (log.isRemovedFromChat) {
      onBack()
    }
  }, [log.isRemovedFromChat, onBack])

  const oldestKey = log.sortedMessages.length
    ? messageKey(log.sortedMessages[0])
    : null

  // One rule governs the viewport: a caught-up reader follows the newest
  // message; a reader who scrolled up stays put, even while older pages
  // prepend above (their height is added back to scrollTop).
  React.useLayoutEffect(() => {
    const list = listRef.current
    if (!list) {
      return
    }
    if (isPinnedRef.current) {
      list.scrollTop = list.scrollHeight
    } else if (oldestKey !== oldestKeyRef.current) {
      list.scrollTop += list.scrollHeight - lastScrollHeightRef.current
    }
    oldestKeyRef.current = oldestKey
    lastScrollHeightRef.current = list.scrollHeight
  }, [log.sortedMessages, oldestKey])

  const userId = session.config.userId

  const mentionCandidates = log.chat.memberIds
    .filter(id => id !== userId)
    .map(id => contacts.get(id))
    .filter((contact): contact is Contact => !!contact)

  const headerSubtitle = (): string => {
    if (log.chat.isGroup) {
      const onlineCount = log.chat.memberIds.filter(
        id => id !== userId && session.inbox.isOnline(id),
      ).length
      return Localization.translator.xMembersYOnline(
        log.chat.memberIds.length,
        onlineCount,
      )
    }

    const recipientId = log.chat.memberIds.find(id => id !== userId)
    return directory.directSubtitle(recipientId)
  }

  const handleArchiveConfirmed = async (): Promise<void> => {
    setPendingAction(null)
    setActionError(null)
    const result = await session.archiveChat(log.chat)
    if (isOk(result)) {
      onBack()
    } else {
      setActionError(Localization.translator.failedToArchive)
    }
  }

  const handleUnarchivePress = async (): Promise<void> => {
    setActionError(null)
    const result = await session.unarchiveChat(log.chat)
    if (isErr(result)) {
      setActionError(Localization.translator.failedToUnarchive)
    }
  }

  // Success needs no navigation here: the local membership update trips the
  // removed-from-chat ejection effect, which pops the screen.
  const handleLeaveConfirmed = async (): Promise<void> => {
    setPendingAction(null)
    setActionError(null)
    const result = await session.leaveChat(log.chat)
    if (isErr(result)) {
      setActionError(Localization.translator.failedToLeave)
    }
  }

  const menuItems = (): { label: string; onPress: () => void }[] => {
    const items: { label: string; onPress: () => void }[] = []
    if (canArchive(log.chat, userId)) {
      items.push(
        log.chat.isArchived
          ? {
              label: Localization.translator.unarchiveConversation,
              onPress: handleUnarchivePress,
            }
          : {
              label: Localization.translator.archiveConversation,
              onPress: () => setPendingAction('archive'),
            },
      )
    }
    if (canLeave(log.chat)) {
      items.push({
        label: Localization.translator.leaveConversation,
        onPress: () => setPendingAction('leave'),
      })
    }
    return items
  }

  const renderHeader = (): React.ReactElement => (
    <div className="tw:flex tw:items-center tw:gap-1.5 tw:px-4 tw:pb-5 tw:pt-3">
      <Button variant="primary-ghost" size="icon" onClick={onBack}>
        <ChevronLeft />
      </Button>
      <div
        className={`tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:items-center ${
          log.chat.isGroup ? 'tw:cursor-pointer' : ''
        }`}
        onClick={
          log.chat.isGroup ? () => onOpenGroupDetails(log.chat) : undefined
        }
      >
        <span className="tw:max-w-full tw:truncate tw:text-base tw:font-semibold tw:text-foreground">
          {log.chat.name || Localization.translator.conversation}
        </span>
        <span className="tw:max-w-full tw:truncate tw:text-sm tw:text-muted-foreground">
          {headerSubtitle()}
        </span>
      </div>
      <Avatar
        src={log.chat.avatar}
        avatarType={log.chat.isGroup ? AvatarType.Company : AvatarType.User}
        isOnline={
          log.chat.isGroup
            ? null
            : session.inbox.isOnline(
                log.chat.memberIds.find(id => id !== userId) || '',
              )
        }
      />
      {menuItems().length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={Localization.translator.actions}
            render={<Button variant="primary-ghost" size="icon" />}
          >
            <MoreVertical />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {menuItems().map(item => (
              <DropdownMenuItem key={item.label} onClick={item.onPress}>
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )

  const renderDaySeparator = (timestamp: number): React.ReactElement => (
    <div className="tw:my-2.5 tw:flex tw:justify-center">
      <span className="tw:rounded-full tw:bg-muted tw:px-4 tw:py-1 tw:text-xs tw:font-semibold tw:text-muted-foreground">
        {projectDateStore.getPronounOrMonthAndDayString(timestamp)}
      </span>
    </div>
  )

  const renderWorkflowCards = (message: ChatMessage): React.ReactNode => {
    if (
      !message.text ||
      !extractWorkflowLinks(message.text, window.location.host).length
    ) {
      return null
    }

    return (
      <div style={{ width: WORKFLOW_CARD_WIDTH, maxWidth: '100%' }}>
        <WorkflowLinkCards
          store={workflowCardStore}
          text={message.text}
          onOpenWorkflow={onOpenWorkflow}
        />
      </div>
    )
  }

  const renderRow = (item: LogRow): React.ReactElement =>
    item.kind === 'day' ? (
      renderDaySeparator(item.timestamp)
    ) : (
      <MessageBubble
        message={item.message}
        isOwn={item.message.authorId === userId}
        isFirstOfRun={item.isFirstOfRun}
        isLastOfRun={item.isLastOfRun}
        showAuthor={
          chat.isGroup && item.message.authorId !== userId && item.isFirstOfRun
        }
        showAvatar={chat.isGroup && item.message.authorId !== userId}
        userId={userId}
        contacts={contacts}
        isAuthorOnline={session.inbox.isOnline(item.message.authorId)}
        projectDateStore={projectDateStore}
        onRetry={fakeId => log.retry(fakeId)}
        onMentionPress={onMentionPress}
        onAttachmentPress={(attachments, index) =>
          showPreview(toImagePreviewFiles(attachments), index)
        }
        belowBubble={renderWorkflowCards(item.message)}
      />
    )

  const renderMessages = (): React.ReactElement => {
    const isLoading = log.status === 'loading' || log.status === 'idle'
    if (isLoading && !log.sortedMessages.length) {
      return <Loader hint={Localization.translator.loading} />
    }

    if (log.status === 'error') {
      return (
        <Empty className="tw:h-full">
          <EmptyHeader>
            <EmptyTitle>
              {Localization.translator.couldNotLoadMessages}
            </EmptyTitle>
          </EmptyHeader>
          <EmptyContent>
            <EmptyAction variant="link" onClick={() => log.open()}>
              {Localization.translator.tryAgain}
            </EmptyAction>
          </EmptyContent>
        </Empty>
      )
    }

    if (!log.sortedMessages.length) {
      return (
        <Empty className="tw:h-full">
          <EmptyHeader>
            <EmptyTitle>{Localization.translator.noMessagesYet}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )
    }

    return (
      <div
        ref={listRef}
        className="tw:flex tw:h-full tw:flex-col tw:overflow-y-auto tw:py-2.5"
        onScroll={handleScroll}
      >
        {log.isLoadingOlder && (
          <div className="tw:flex tw:justify-center tw:py-2.5">
            <Spinner size="sm" />
          </div>
        )}
        {buildRows(log.sortedMessages).map(item => (
          <React.Fragment key={item.key}>{renderRow(item)}</React.Fragment>
        ))}
      </div>
    )
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    isPinnedRef.current =
      scrollHeight - clientHeight - scrollTop < PIN_THRESHOLD_PX
    lastScrollHeightRef.current = scrollHeight
    if (scrollTop < LOAD_OLDER_THRESHOLD_PX) {
      log.loadOlder()
    }
  }

  const handleSend = (message: string, attachments: Attachment[]): void => {
    log.sendMessage(message, attachments)
    isPinnedRef.current = true
  }

  return (
    <div className="tw:relative tw:flex tw:h-full tw:flex-col">
      {renderHeader()}
      {!!actionError && (
        <span className="tw:pb-1 tw:text-center tw:text-xs tw:text-destructive">
          {actionError}
        </span>
      )}
      <div className="tw:min-h-0 tw:flex-1 tw:px-4">{renderMessages()}</div>
      {log.chat.isArchived ? (
        <div className="tw:flex tw:items-center tw:justify-center tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-muted tw:py-4">
          <span className="tw:text-base tw:text-muted-foreground">
            {Localization.translator.conversationIsArchived}
          </span>
        </div>
      ) : (
        <ComposerBar
          mentionCandidates={mentionCandidates}
          tray={tray}
          shouldSendOnEnter={shouldSendOnEnter}
          onSend={handleSend}
        />
      )}
      <ConfirmDialog
        isOpen={pendingAction === 'archive'}
        title={Localization.translator.archiveConversation}
        question={Localization.translator.archiveConversationConfirmation}
        doneTitle={Localization.translator.archive}
        useRedConfirmationButton={true}
        onCancelClicked={() => setPendingAction(null)}
        onDoneClicked={handleArchiveConfirmed}
      />
      <ConfirmDialog
        isOpen={pendingAction === 'leave'}
        title={Localization.translator.leaveConversation}
        question={Localization.translator.leaveConversationConfirmation}
        doneTitle={Localization.translator.leave_verb}
        useRedConfirmationButton={true}
        onCancelClicked={() => setPendingAction(null)}
        onDoneClicked={handleLeaveConfirmed}
      />
    </div>
  )
}

export default observer(ChatLogScreen)
